import type {
  ParentHasAvailableChildrenError,
  PropertyChildInput,
} from "../types/propertyChildren";
import {
  PARENT_HAS_AVAILABLE_CHILDREN_CODE,
  TOWER_BLOCK_MAX_LENGTH,
} from "../types/propertyChildren";

export function buildUnitReference(
  floor: string,
  apartmentNumber: string,
  towerBlock?: string | null
): string {
  const tower = String(towerBlock ?? "").trim();
  const floorPart = `Andar ${floor.trim()}`;
  const aptPart = `Apto ${apartmentNumber.trim()}`;

  if (tower) {
    return `Torre/Bloco ${tower} - ${floorPart} - ${aptPart}`;
  }

  return `${floorPart} - ${aptPart}`;
}

export function normalizeChildKey(
  floor: string,
  apartmentNumber: string,
  towerBlock?: string | null
): string {
  return [
    String(towerBlock ?? "").trim().toLowerCase(),
    floor.trim().toLowerCase(),
    apartmentNumber.trim().toLowerCase(),
  ].join("|");
}

export function validateChildInput(child: PropertyChildInput): string | null {
  if (!child.floor?.trim()) return "Informe o andar da unidade.";
  if (!child.apartment_number?.trim()) {
    return "Informe o número do apartamento.";
  }
  const tower = child.tower_block?.trim() ?? "";
  if (tower.length > TOWER_BLOCK_MAX_LENGTH) {
    return `Torre/bloco deve ter no máximo ${TOWER_BLOCK_MAX_LENGTH} caracteres.`;
  }
  return null;
}

export function findDuplicateChildIndex(
  children: PropertyChildInput[],
  candidate: PropertyChildInput,
  ignoreIndex?: number
): number {
  const candidateKey = normalizeChildKey(
    candidate.floor,
    candidate.apartment_number,
    candidate.tower_block
  );

  return children.findIndex((child, index) => {
    if (ignoreIndex != null && index === ignoreIndex) return false;
    return (
      normalizeChildKey(
        child.floor,
        child.apartment_number,
        child.tower_block
      ) === candidateKey
    );
  });
}

export function validateChildrenList(
  children: PropertyChildInput[]
): string | null {
  for (let i = 0; i < children.length; i += 1) {
    const fieldError = validateChildInput(children[i]);
    if (fieldError) return fieldError;

    if (findDuplicateChildIndex(children, children[i], i) >= 0) {
      return "Já existe uma unidade com a mesma torre, andar e apartamento.";
    }
  }
  return null;
}

export function parseParentHasAvailableChildrenError(
  err: unknown
): ParentHasAvailableChildrenError | null {
  const response = (err as { response?: { status?: number; data?: unknown } })
    ?.response;

  if (response?.status !== 409) return null;

  const data = response.data as
    | {
        code?: string;
        message?: string;
        data?: { available_children_count?: number };
      }
    | undefined;

  const message = data?.message ?? "";
  const hasChildrenCode = data?.code === PARENT_HAS_AVAILABLE_CHILDREN_CODE;
  const hasChildrenHint =
    /confirm_with_available_children|unidades filhas|filhos/i.test(message);

  if (!hasChildrenCode && !hasChildrenHint) return null;

  return {
    code: PARENT_HAS_AVAILABLE_CHILDREN_CODE,
    availableChildrenCount: Number(data?.data?.available_children_count ?? 0),
    message:
      message ||
      "Este imóvel possui unidades filhas disponíveis. Confirme para continuar.",
  };
}
